import { useMatches, type Params, type RouteObject } from "react-router";
import { type Routes } from "..";
import type { CleanPath, JoinAndCleanPaths } from "./path";

// Recursive type to extract all possible path strings from a route configuration array
export type ExtractHandles<
  // Use ReadonlyArray<AppRouteObject> to match the structure, especially if using `as const`
  TRoutes extends readonly RouteObject[],
  TBasePath extends string = "/", // Start with root path '/'
> = {
  // Iterate over each route object in the input array
  [K in keyof TRoutes]: TRoutes[K] extends infer R
    ? R extends RouteObject // Ensure R is a valid route object
      ? // Determine the full path for the current route R
        | (R extends { path: infer P extends string; handle: infer H } // Case 1: Route has a 'path' property
              ? { path: JoinAndCleanPaths<TBasePath, P>; handle: H } // Join base path with the route's path and clean, adding handle
              : R extends { index: true } // Case 2: Route has 'index: true'
                ? CleanPath<TBasePath> // Index route's path is the cleaned base path
                : CleanPath<TBasePath>) // Case 3: Layout Route (no path/index) - Path is the cleaned base path
          // Recursively extract paths from children, if they exist
          | (R extends { children: infer C extends readonly RouteObject[] }
              ? ExtractHandles<
                  C,
                  // Determine the base path for the children
                  R extends { path: infer P extends string } // If parent had a path...
                    ? JoinAndCleanPaths<TBasePath, P> // ...use its cleaned full path
                    : CleanPath<TBasePath> // If parent was index/layout, children inherit cleaned base path
                >
              : never) // No children, no recursive paths
      : never // Should not happen if TRoutes is well-typed
    : never;
}[number]; // Use [number] to get a union of all calculated paths from the iteration

type Handles = ExtractHandles<Routes>;

export type HandlePaths = Extract<Handles, { path: string }>["path"];

type AllHandles = Extract<Handles, { handle: object }>["handle"];
type HandleKeys = AllHandles extends infer H ? (H extends object ? keyof H : never) : never;
type HandleValue<K extends HandleKeys> = AllHandles extends infer H
  ? H extends Record<K, infer V>
    ? V
    : never
  : never;

type RouteWithHandle<K extends HandleKeys> = {
  id: string;
  pathname: string;
  params: Params<string>;
  data: unknown;
  handle: Record<K, HandleValue<K>>;
};

export function useHandle<K extends HandleKeys>(key: K): HandleValue<K> | undefined {
  const match = useMatches()
    .filter(
      (m): m is RouteWithHandle<K> =>
        Boolean(m.handle) && typeof m.handle === "object" && m.handle !== null && key in m.handle,
    )
    .at(-1);
  return match?.handle[key];
}
